/**
 * Centralized time handling for the panel.
 *
 * The server stores timestamps as SQLite/MariaDB DATETIME in UTC, formatted as
 * "YYYY-MM-DD HH:MM:SS" WITHOUT a timezone marker. If you feed that straight into
 * `new Date(str)`, the browser interprets it as LOCAL time, so every displayed
 * timestamp is wrong by the viewer's UTC offset.
 *
 * `parseServerDate` normalizes any server datetime (bare SQLite string, ISO with
 * or without 'Z', or a Date/epoch) into a correct Date anchored to UTC. Every
 * formatter below funnels through it and renders in the VIEWER'S local timezone.
 *
 * For values that are genuinely "now" on the client (e.g. a toast timestamp),
 * don't use these — just use `new Date()`; those are already local and correct.
 */

/**
 * Parse a server-provided timestamp into a Date, treating bare (no-tz) strings
 * as UTC. Returns null for empty/invalid input.
 */
export const parseServerDate = (value) => {
    if (value == null || value === '') return null;

    // Already a Date
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

    // Numeric epoch (ms or seconds)
    if (typeof value === 'number') {
        // Heuristic: seconds vs milliseconds
        const ms = value < 1e12 ? value * 1000 : value;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
    }

    let str = String(value).trim();
    if (!str) return null;

    // If it already carries an explicit timezone (Z or +hh:mm / -hh:mm after the
    // time part), trust it as-is.
    const hasTz = /[zZ]$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str.slice(11));

    if (!hasTz) {
        // A bare "YYYY-MM-DD" is a CALENDAR date, not an instant. Parse it as LOCAL
        // midnight so it displays as the same date in every timezone (appending 'Z'
        // would make it UTC midnight and shift a day for negative-offset viewers).
        const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnly) {
            const [, y, mo, da] = dateOnly;
            return new Date(Number(y), Number(mo) - 1, Number(da));
        }
        // "YYYY-MM-DD HH:MM:SS" is an instant stored in UTC -> mark it UTC so the
        // browser converts to the viewer's local time.
        str = str.replace(' ', 'T');
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(str)) {
            str = str + 'Z';
        }
    }

    const d = new Date(str);
    if (isNaN(d.getTime())) {
        // Last resort: try the raw value
        const fallback = new Date(String(value));
        return isNaN(fallback.getTime()) ? null : fallback;
    }
    return d;
};

/**
 * Full local date + time. Format follows the viewer's locale.
 * e.g. "17/07/2026, 06:22:34 PM" (locale-dependent)
 */
export const formatLocalDateTime = (value, opts) => {
    const d = parseServerDate(value);
    if (!d) return '—';
    return d.toLocaleString(undefined, opts || {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
};

/** Local time only, e.g. "18:22:34". Defaults to 24h with seconds. */
export const formatLocalTime = (value, opts) => {
    const d = parseServerDate(value);
    if (!d) return '—';
    return d.toLocaleTimeString(undefined, opts || {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
};

/** Local date only, e.g. "17/07/2026". */
export const formatLocalDate = (value, opts) => {
    const d = parseServerDate(value);
    if (!d) return '—';
    return d.toLocaleDateString(undefined, opts);
};

/** Compact date + time, e.g. "Jul 17, 06:22 PM". */
export const formatShortDateTime = (value) => {
    const d = parseServerDate(value);
    if (!d) return '—';
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

/**
 * Relative time from now, e.g. "3m ago", "2h ago", "just now".
 * Falls back to a short date for anything older than ~7 days.
 */
export const formatRelative = (value) => {
    const d = parseServerDate(value);
    if (!d) return '—';
    const diffMs = Date.now() - d.getTime();
    const sec = Math.round(diffMs / 1000);
    if (sec < 0) return 'just now';
    if (sec < 45) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    if (day < 7) return `${day}d ago`;
    return formatLocalDate(d);
};

/** The viewer's IANA timezone name, e.g. "Europe/Berlin". */
export const getLocalTimezone = () => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
    catch (_) { return ''; }
};

/** Short timezone abbreviation for `date`, e.g. "CEST", "GMT+5:30". */
export const getTimezoneAbbr = (date) => {
    try {
        const d = date instanceof Date ? date : new Date();
        return new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
            .formatToParts(d)
            .find(p => p.type === 'timeZoneName')?.value || '';
    } catch (_) { return ''; }
};
